import axios from "axios";
import { acquireAppAccessToken } from "../auth/authService";

const graphPhotoUrlCache = new Map<string, string>();
const graphPhotoPromiseCache = new Map<string, Promise<string | undefined>>();
const graphPhotoFailureCache = new Set<string>();

const GRAPH_PHOTO_SCOPE = "User.ReadBasic.All";
const INTERNAL_EMAIL_DOMAIN = "@deloitte.com";

const normalizeGraphUserId = (userId: string) => userId.trim().toLowerCase();

const isInternalDeloitteUser = (userId: string) =>
  normalizeGraphUserId(userId).endsWith(INTERNAL_EMAIL_DOMAIN);

export const canAttemptGraphUserPhotoLookup = (userId: string) => {
  if (!userId || !userId.trim()) {
    return false;
  }

  const normalizedUserId = normalizeGraphUserId(userId);

  if (!isInternalDeloitteUser(normalizedUserId)) {
    return false;
  }

  return !graphPhotoFailureCache.has(normalizedUserId);
};

const createObjectUrlFromPhotoResponse = (response: { data: ArrayBuffer; headers: Record<string, string> }) => {
  const contentType = response.headers?.["content-type"] || "image/jpeg";
  const photoBlob = new Blob([response.data], { type: contentType });
  return URL.createObjectURL(photoBlob);
};

export const getGraphUserPhotoUrl = async (userId: string): Promise<string | undefined> => {
  if (!canAttemptGraphUserPhotoLookup(userId)) {
    return undefined;
  }

  const normalizedUserId = normalizeGraphUserId(userId);

  const cachedPhotoUrl = graphPhotoUrlCache.get(normalizedUserId);
  if (cachedPhotoUrl) {
    return cachedPhotoUrl;
  }

  const existingPromise = graphPhotoPromiseCache.get(normalizedUserId);
  if (existingPromise) {
    return existingPromise;
  }

  const fetchPromise = (async () => {
    try {
      const graphToken = await acquireAppAccessToken(GRAPH_PHOTO_SCOPE);
      if (!graphToken) {
        console.warn(`[GraphPhotoService] Could not acquire access token for scope "${GRAPH_PHOTO_SCOPE}". Check MSAL config / Azure AD consent in this environment.`);
        return undefined;
      }

      const photoResponse = await axios.get<ArrayBuffer>(
        `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(userId.trim())}/photos/240x240/$value`,
        {
          headers: {
            Authorization: `Bearer ${graphToken}`,
          },
          responseType: "arraybuffer",
        }
      );

      const photoUrl = createObjectUrlFromPhotoResponse({
        data: photoResponse.data,
        headers: photoResponse.headers as Record<string, string>,
      });

      graphPhotoUrlCache.set(normalizedUserId, photoUrl);
      return photoUrl;
    } catch(error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404) {
        graphPhotoFailureCache.add(normalizedUserId);
      } else {
        // For 401/403/network errors, also cache the failure to avoid repeated calls.
        // Log so the issue is visible in the browser console (especially useful in non-local environments).
        graphPhotoFailureCache.add(normalizedUserId);
        console.warn(
          `[GraphPhotoService] Photo fetch failed for "${userId}"`,
          status ? `HTTP ${status}` : error
        );
      }
      return undefined;
    } finally {
      graphPhotoPromiseCache.delete(normalizedUserId);
    }
  })();

  graphPhotoPromiseCache.set(normalizedUserId, fetchPromise);
  return fetchPromise;
};
